import { useState } from 'react'
import ReactPlayer from 'react-player'
import styles from './Videos.module.css'
import SectionHeader from './SectionHeader'
import Carousel from './Carousel'
import { formatVideoDuration, videoHref, videoTypeLabel } from 'lib/dealhunt/video'
import HighlightText from 'components/common/HighlightText'
import type { DealHuntVideo } from 'lib/dealhunt/types'
import { useCatalogEvent } from 'lib/analytics/catalog'
import type { CatalogEventParams } from 'shared-code/app-events'

interface VideosProps {
  videos: DealHuntVideo[]
  /** Product name to emphasise where it's mentioned in a video title. */
  productName: string
}

interface VideoCardProps {
  video: DealHuntVideo
  href: string
  productName: string
  position: number
}

/**
 * A single video card. Shows the static thumbnail (with type pill, play glyph,
 * duration) until hovered, then swaps in a muted, autoplaying inline preview —
 * muted is required for hover-autoplay (browser autoplay policy). The card stays
 * a link, so the title still opens the full video on YouTube.
 */
const VideoCard: React.VFC<VideoCardProps> = ({ video, href, productName, position }) => {
  const [preview, setPreview] = useState(false)
  const { track } = useCatalogEvent()
  const videoParams: CatalogEventParams = {
    target_id: video.videoId ?? null,
    target_title: video.title,
    destination_url: href,
    position,
  }
  const duration = formatVideoDuration(video.durationSeconds)
  const type = videoTypeLabel(video.videoType)

  return (
    <a
      className={styles.card}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseEnter={() => setPreview(true)}
      onMouseLeave={() => setPreview(false)}
      onClick={() => track('product_page_video_click', videoParams)}
    >
      <span className={styles.thumb}>
        {preview ? (
          <ReactPlayer
            className={styles.player}
            src={href}
            playing
            muted
            controls
            width="100%"
            height="100%"
            onPlay={() => track('product_page_video_preview', videoParams)}
          />
        ) : (
          <>
            {video.thumbnailUrl ? (
              // eslint-disable-next-line @next/next/no-img-element
              <img src={video.thumbnailUrl} alt="" className={styles.thumbImg} loading="lazy" />
            ) : (
              <span className={styles.thumbEmpty} aria-hidden="true" />
            )}
            {type && <span className={styles.typePill}>{type}</span>}
            <span className={styles.play} aria-hidden="true">
              <span className={styles.playTri} />
            </span>
            {duration && <span className={styles.duration}>{duration}</span>}
          </>
        )}
      </span>
      <span className={styles.body}>
        <span className={styles.title}>
          <HighlightText text={video.title ?? 'Watch review'} query={productName} exact />
        </span>
        {video.channelTitle && <span className={styles.channel}>{video.channelTitle}</span>}
      </span>
    </a>
  )
}

/** Horizontal-scrolling video cards sourced from DealHunt enrichment.videos. */
const Videos: React.VFC<VideosProps> = ({ videos, productName }) => {
  const items = videos
    .map(v => ({ video: v, href: videoHref(v) }))
    .filter((i): i is { video: DealHuntVideo; href: string } => Boolean(i.href))
  if (!items.length) {
    return null
  }

  return (
    <section className={styles.section}>
      <SectionHeader
        title="Videos"
        link={{
          href: 'https://www.youtube.com/@SoundGuys',
          label: 'Visit the SoundGuys channel ›',
        }}
        intro="Video reviews and comparisons for this product."
      />

      <Carousel>
        {items.map(({ video, href }, i) => (
          <VideoCard
            key={video.videoId ?? href ?? video.title ?? ''}
            video={video}
            href={href}
            productName={productName}
            position={i + 1}
          />
        ))}
      </Carousel>
    </section>
  )
}

export default Videos
